import priorityFlashImage from '../assets/images/priority_flash.png';

/**
 * The priority flash recolored. `assets/images/priority_flash.png` is a 1x1 animated png that fades
 * red in and out; it is painted as a cell's background image, so every flashing cell shares one image
 * animation and the browser does almost no work per cell. A configured color gets its own copy of
 * that animation, built here, so it flashes with the same timing at the same cost. Unlike the red png,
 * it never fades below 45%, per the final design.
 *
 * Painting the red png through a CSS mask instead looked the same but needed a compositing layer per
 * cell, and dropped to ~30fps at around 700 flashing cells.
 */

// The alpha and delay (ms) of each frame of priority_flash.png, in order.
const PRIORITY_FLASH_PNG_ALPHAS = [
    0, 1, 2, 4, 6, 8, 11, 13, 17, 20, 24, 28, 32, 37, 42, 47, 52, 58, 63, 69, 75, 81, 88, 94, 100,
    107, 114, 120, 127, 134, 140, 147, 154, 160, 166, 173, 179, 185, 191, 196, 202, 207, 212, 217,
    222, 226, 230, 234, 237, 241, 243, 246, 248, 250, 252, 253, 254, 255, 254, 253, 252, 250, 248,
    246, 243, 241, 237, 234, 230, 226, 222, 217, 212, 207, 202, 196, 191, 185, 179, 173, 166, 160,
    154, 147, 140, 134, 127, 120, 114, 107, 100, 94, 88, 81, 75, 69, 63, 58, 52, 47, 42, 37, 32, 28,
    24, 20, 17, 13, 11, 8, 6, 4, 2, 1, 0,
];
const FLOOR_ALPHA = 115;
const FRAME_ALPHAS = PRIORITY_FLASH_PNG_ALPHAS.map(function raiseToFloor(alpha) {
    return Math.round(FLOOR_ALPHA + (alpha * (255 - FLOOR_ALPHA)) / 255);
});
const FRAME_DELAYS_MS = FRAME_ALPHAS.map(function frameDelay(_alpha, index) {
    if (index === 0) {
        return 48;
    }
    // The frames either side of the peak, and the last frame, hold a little longer.
    return index === 56 || index === 58 || index === FRAME_ALPHAS.length - 1 ? 32 : 16;
});

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

const CRC_TABLE = Array.from({ length: 256 }, function crcEntry(_value, n) {
    let c = n;
    for (let k = 0; k < 8; k++) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    return c >>> 0;
});

function crc32(bytes: number[]): number {
    let crc = 0xffffffff;
    bytes.forEach(function addByte(byte) {
        crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
    });
    return (crc ^ 0xffffffff) >>> 0;
}

function uint32(value: number): number[] {
    return [(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff];
}

function uint16(value: number): number[] {
    return [(value >>> 8) & 0xff, value & 0xff];
}

function chunk(type: string, data: number[]): number[] {
    const typeAndData = [...Array.from(type, (char) => char.charCodeAt(0)), ...data];
    return [...uint32(data.length), ...typeAndData, ...uint32(crc32(typeAndData))];
}

// One unfiltered RGBA pixel as a zlib stream holding a single uncompressed block.
function zlibPixel(red: number, green: number, blue: number, alpha: number): number[] {
    const scanline = [0, red, green, blue, alpha];
    let a = 1;
    let b = 0;
    scanline.forEach(function addToAdler(byte) {
        a = (a + byte) % 65521;
        b = (b + a) % 65521;
    });
    const length = scanline.length;
    return [
        0x78,
        0x01,
        0x01,
        length & 0xff,
        length >>> 8,
        ~length & 0xff,
        (~length >>> 8) & 0xff,
        ...scanline,
        ...uint32(((b << 16) | a) >>> 0),
    ];
}

function parseHex(hex: string): [number, number, number] | null {
    const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!match) {
        return null;
    }
    const value = parseInt(match[1], 16);
    return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

export function buildPriorityFlashPng(hex: string): Uint8Array | null {
    const rgb = parseHex(hex);
    if (!rgb) {
        return null;
    }
    const [red, green, blue] = rgb;
    const bytes: number[] = [
        ...PNG_SIGNATURE,
        // 1x1, 8-bit RGBA, no interlace.
        ...chunk('IHDR', [...uint32(1), ...uint32(1), 8, 6, 0, 0, 0]),
        // Loops forever.
        ...chunk('acTL', [...uint32(FRAME_ALPHAS.length), ...uint32(0)]),
    ];
    let sequence = 0;
    FRAME_ALPHAS.forEach(function addFrame(alpha, index) {
        bytes.push(
            ...chunk('fcTL', [
                ...uint32(sequence++),
                ...uint32(1),
                ...uint32(1),
                ...uint32(0),
                ...uint32(0),
                ...uint16(FRAME_DELAYS_MS[index]),
                ...uint16(1000),
                0,
                0,
            ])
        );
        const pixel = zlibPixel(red, green, blue, alpha);
        bytes.push(
            ...(index === 0
                ? chunk('IDAT', pixel)
                : chunk('fdAT', [...uint32(sequence++), ...pixel]))
        );
    });
    bytes.push(...chunk('IEND', []));
    return Uint8Array.from(bytes);
}

const imagesByHex = new Map<string, string | null>();

/** A data URL for the priority flash in `hex`, or null if `hex` isn't a #rrggbb color. */
export function getPriorityFlashImage(hex: string): string | null {
    const key = hex.toLowerCase();
    const cached = imagesByHex.get(key);
    if (cached !== undefined) {
        return cached;
    }
    const png = buildPriorityFlashPng(key);
    const image = png ? `data:image/png;base64,${btoa(String.fromCharCode(...png))}` : null;
    imagesByHex.set(key, image);
    return image;
}

/** The CSS background-image for the priority flash in `flashColor` (#rrggbb). Defaults to red. */
export function getPriorityFlashBackgroundImage(flashColor: string | undefined): string {
    const image = (flashColor && getPriorityFlashImage(flashColor)) || priorityFlashImage;
    return `url(${image})`;
}
